import { useEffect, useState } from "react";
import api, { eur, hrs, fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TimesheetDetail } from "@/components/TimesheetDetail";
import { CorrectEntryDialog } from "@/components/CorrectEntryDialog";
import { AssignEmployeeDialog } from "@/components/AssignEmployeeDialog";
import { LoggedByHint } from "@/components/LoggedByHint";
import { Lock, LockOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Timesheets() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("all");
  const [emp, setEmp] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sel, setSel] = useState([]);
  const [delTs, setDelTs] = useState(null);
  const [bulkDel, setBulkDel] = useState(false);
  const [viewTs, setViewTs] = useState(null);
  const [corrIdx, setCorrIdx] = useState(null);
  const [assignTs, setAssignTs] = useState(null);
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [needsCount, setNeedsCount] = useState(0);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const loadCount = () => api.get("/timesheets/needs-attention-count").then((r) => setNeedsCount(r.data.count)).catch(() => {});
  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (dateFrom) params.start = dateFrom;
    if (dateTo) params.end = dateTo;
    if (emp !== "all") params.employee_id = emp;
    if (unlinkedOnly) params.unlinked = true;
    api.get("/timesheets", { params }).then((r) => { setRows(r.data); setSel([]); });
    loadCount();
  };
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); }, []);
  useEffect(() => { load(); }, [status, dateFrom, dateTo, emp, unlinkedOnly]);

  const empRec = (id) => employees.find((x) => x.id === id);
  const empName = (id) => { const e = empRec(id); return e ? `${e.first_name} ${e.surname}` : "—"; };
  const openView = async (id) => { try { const { data } = await api.get(`/timesheets/${id}`); setViewTs(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const lock = async (id) => { try { await api.post(`/timesheets/${id}/lock`, {}); toast.success("Locked"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unlock = async (id) => { try { await api.post(`/timesheets/${id}/unlock`, {}); toast.success("Unlocked"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doDelete = async () => { try { await api.delete(`/timesheets/${delTs.id}`); toast.success("Timesheet deleted"); setDelTs(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const [recalcing, setRecalcing] = useState(false);
  const recalcOpen = async () => {
    setRecalcing(true);
    try {
      const { data } = await api.post("/admin/recalculate-open-timesheets", {});
      toast.success(`Recalculated ${data.updated} open timesheet${data.updated === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRecalcing(false); }
  };

  const toggleOne = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = rows.length > 0 && sel.length === rows.length;
  const toggleAll = () => setSel(allSelected ? [] : rows.map((r) => r.id));

  const runBulk = async (action) => {
    if (sel.length === 0) return;
    try {
      const { data } = await api.post("/timesheets/bulk-action", { ids: sel, action });
      const verb = { lock: "Locked", unlock: "Unlocked", delete: "Deleted" }[action];
      toast.success(`${verb} ${data.done}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setBulkDel(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout nav={adminNav} title="Timesheets">
      <PageHeader title="All Timesheets" subtitle={`${rows.length} timesheets`}>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="ts-needs-attention" variant="outline" size="sm" className={unlinkedOnly ? "bg-amber-500 text-black border-amber-500 hover:bg-amber-600" : "text-amber-700 border-amber-200 hover:bg-amber-50"} onClick={() => setUnlinkedOnly((v) => !v)}>Needs attention{needsCount ? ` (${needsCount})` : ""}</Button>
          <Button data-testid="ts-recalc-open" variant="outline" size="sm" disabled={recalcing} onClick={recalcOpen}>{recalcing ? "Recalculating…" : "Recalculate open timesheets"}</Button>
          <div className="flex items-center gap-1">
            <Input data-testid="ts-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-9" />
            <span className="text-zinc-400 text-sm">–</span>
            <Input data-testid="ts-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-9" />
          </div>
          <Select value={emp} onValueChange={setEmp}>
            <SelectTrigger data-testid="ts-employee-filter" className="w-48"><SelectValue placeholder="Employee" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.surname}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="ts-status-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "draft", "submitted", "approved", "returned", "rejected", "locked"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
          {(dateFrom || dateTo || status !== "all" || emp !== "all" || unlinkedOnly) && <Button data-testid="ts-clear-filters" variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setStatus("all"); setEmp("all"); setUnlinkedOnly(false); }}>Clear</Button>}
        </div>
      </PageHeader>

      {sel.length > 0 && (
        <div data-testid="ts-bulk-bar" className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-sm font-semibold text-slate-700">{sel.length} selected</span>
          <div className="flex-1" />
          <Button data-testid="bulk-assign" size="sm" variant="outline" className="text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => setBulkAssignOpen(true)}>Assign to employee</Button>
          <Button data-testid="bulk-lock" size="sm" variant="outline" onClick={() => runBulk("lock")}><Lock className="h-3.5 w-3.5 mr-1" />Lock</Button>
          <Button data-testid="bulk-unlock" size="sm" variant="outline" onClick={() => runBulk("unlock")}><LockOpen className="h-3.5 w-3.5 mr-1" />Unlock</Button>
          <Button data-testid="bulk-delete" size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setBulkDel(true)}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
          <Button data-testid="bulk-clear" size="sm" variant="ghost" onClick={() => setSel([])}>Clear</Button>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3 w-10"><Checkbox data-testid="ts-select-all" checked={allSelected} onCheckedChange={toggleAll} /></th>
            <th className="p-3">Employee</th><th className="p-3">Week</th><th className="p-3 text-right">Hours</th><th className="p-3 text-right">Wages</th><th className="p-3 text-right">Charge</th><th className="p-3 text-right">Loaded Cost</th><th className="p-3">Status</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className={`border-b border-zinc-50 hover:bg-zinc-50 ${sel.includes(t.id) ? "bg-amber-50/60" : ""}`}>
                <td className="p-3"><Checkbox data-testid={`ts-select-${t.id}`} checked={sel.includes(t.id)} onCheckedChange={() => toggleOne(t.id)} /></td>
                <td className="p-3 font-medium">{empRec(t.employee_id) ? empName(t.employee_id) : (
                  <div className="space-y-1">
                    <span className="flex items-center gap-2">
                      <span data-testid={`ts-unlinked-${t.id}`} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Unlinked</span>
                      <Button data-testid={`ts-assign-${t.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAssignTs(t)}>Assign employee</Button>
                    </span>
                    <LoggedByHint t={t} testid={`ts-loggedby-${t.id}`} />
                  </div>
                )}</td>
                <td className="p-3 num">{fmtDate(t.week_start)}</td>
                <td className="p-3 num text-right">{hrs(t.total_paid_hours)}</td>
                <td className="p-3 num text-right">{eur(t.total_wages)}</td>
                <td className="p-3 num text-right">{eur(t.total_charge)}</td>
                <td className="p-3 num text-right">{eur(t.total_cost)}</td>
                <td className="p-3"><StatusBadge status={t.status} /></td>
                <td className="p-3 text-right"><div className="flex gap-1 justify-end">
                  <Button data-testid={`ts-view-${t.id}`} size="sm" variant="outline" onClick={() => openView(t.id)}>View</Button>
                  {t.status === "approved" && <Button data-testid={`lock-${t.id}`} size="sm" variant="outline" onClick={() => lock(t.id)}>Lock</Button>}
                  {t.status === "locked" && <Button data-testid={`unlock-${t.id}`} size="sm" variant="outline" onClick={() => unlock(t.id)}>Unlock</Button>}
                  {t.status !== "locked" && t.status !== "exported" && <Button data-testid={`ts-delete-${t.id}`} size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDelTs(t)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-zinc-400" data-testid="ts-empty">No timesheets match these filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!delTs} onOpenChange={(v) => !v && setDelTs(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete timesheet?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">This permanently removes {delTs ? empName(delTs.employee_id) : "this"}'s timesheet for week {delTs ? fmtDate(delTs.week_start) : ""}. This cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDelTs(null)}>Cancel</Button>
            <Button data-testid="confirm-delete-ts" className="bg-rose-600 text-white hover:bg-rose-700" onClick={doDelete}>Delete permanently</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDel} onOpenChange={setBulkDel}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete {sel.length} timesheet{sel.length > 1 ? "s" : ""}?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">This permanently deletes the selected timesheets. Locked or exported ones are skipped. This cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setBulkDel(false)}>Cancel</Button>
            <Button data-testid="confirm-bulk-delete" className="bg-rose-600 text-white hover:bg-rose-700" onClick={() => runBulk("delete")}>Delete permanently</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!viewTs} onOpenChange={(v) => !v && setViewTs(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto kqm-scroll">
          {viewTs && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">Timesheet — {empName(viewTs.employee_id)} · week {fmtDate(viewTs.week_start)} <StatusBadge status={viewTs.status} /></DialogTitle>
                <DialogDescription>{["approved", "locked", "exported"].includes(viewTs.status) ? "Approved week. Use Correct on an entry to fix a mistake; every change is recorded with a reason." : "Read-only day-by-day breakdown. No changes are made from this view."}</DialogDescription>
              </DialogHeader>
              <TimesheetDetail ts={viewTs} onCorrect={setCorrIdx} />
            </>
          )}
        </DialogContent>
      </Dialog>
      <CorrectEntryDialog ts={viewTs} index={corrIdx} onOpenChange={(v) => !v && setCorrIdx(null)} onDone={(d) => { setViewTs(d); setCorrIdx(null); load(); }} />

      <AssignEmployeeDialog open={!!assignTs} onOpenChange={(v) => !v && setAssignTs(null)} timesheet={assignTs || {}} employees={employees} onAssigned={load} />
      <AssignEmployeeDialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen} bulkIds={sel} employees={employees} onAssigned={load} />

    </AdminLayout>
  );
}
